import _ from 'lodash';

export const pageStateParam = 'pageState';

/*
 * Private functions.
 */

/**
 * Escape a parameter value so that it can be used in the page state URL parameter.
 *
 * @param {String} value The value to escape.
 *
 * @returns {String} The escaped value.
 *
 * @private
 */
function escapeValue(value) {
    if (_.isNil(value)) return '';

    // Replace any instances of the value delimiter so they don't confuse the parameter parser.  This just
    // replaces the delimiter with a double delimiter, since the parser will only recognize a single delimiter
    // character as an actual delimiter.
    return String(value).replace(/\|{1}/g, '||');
}

/**
 * Unescape a page state URL parameter value.
 *
 * @param {String} escapedEncodedValue The previously escaped value value to unescape.
 *
 * @returns {String} The unescaped filter state value.
 *
 * @private
 */
function unescapeValue(escapedEncodedValue) {
    // Undoes the escaping process described in the escapeValue function.  See that function for more
    // information.
    const value = escapedEncodedValue.replace(/\|{2}/g, '|');

    // Attempt to convert numeric values to numbers.
    const num = Number(value);

    return _.isNaN(num) ? value : num;
}

/*
 * Exported functions.
 */

export function buildUrlParamValue(pageStateValues) {
    return _(pageStateValues)
        .omitBy((value) => value === undefined)
        .map((value, key) => `${key}:${escapeValue(value)}`)
        .join('|');
}

export function parseUrlParamValue(urlParam) {
    // This regular expression is complex.  Here's an explanation of each piece:
    // (?:^|\|) Ensures that the param name is preceded by either the start of the string or a delimiter character
    // ([^:]*?) Captures the parameter name (any character that is not a : character)
    // ((?:[^|]|\|{2})*?) Captures the parameter's encoded value (any character that is not a single delimiter character)
    // (?:\|{1}[^|]|$)) Ensures the parameter's encoded value is followed by a single delimiter or the end of the string
    // The last two pieces are necessary because a parameter's encoded value can contain escaped delimiters, which are
    // escaped by replacing the delimiter with two delimiters.  These pieces ensure only a single delimiter can
    // delineate the encoded state values.
    const paramRegex = new RegExp('(?:^|\\||)([^:]*?):((?:[^|]|\\|{2})*?)(?:\\|(?!\\|)|$)', 'g');
    const decodedState = {};
    let match = paramRegex.exec(urlParam);

    while (match) {
        decodedState[match[1]] = unescapeValue(match[2]);
        match = paramRegex.exec(urlParam);
    }

    return decodedState;
}

export function getStateFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has(pageStateParam)) {
        return parseUrlParamValue(urlParams.get(pageStateParam));
    }

    return undefined;
}

export function getUrlParamFromState(pageState, currentParams = '') {
    const urlParamValue = buildUrlParamValue(pageState);
    const urlParams = new URLSearchParams(currentParams);

    if (_.isEmpty(urlParamValue)) {
        urlParams.delete(pageStateParam);
    } else {
        urlParams.set(pageStateParam, urlParamValue);
    }

    return urlParams.toString();
}

export function setUrlFromState(pageState) {
    const path = window.location.pathname;
    const urlParams = getUrlParamFromState(pageState, window.location.search);
    const newQuery = _.isEmpty(urlParams) ? '' : `?${urlParams}`;
    window.history.replaceState(null, '', `${path}${newQuery}`);
}
